import React, { useState, useEffect, useMemo } from "react";
import { Appointment, FollowUpStatusItem, Professional, TimeSlot } from "../../../types";
import { availabilityApi, appointmentsApi } from "../../../services";
import { formatTime, parseTime, isBirthdayToday } from "../../../utils/dateUtils";
import { getPaymentMethodColor } from "../../../utils/appointmentUtils";
import { useCompany } from "../../../contexts/CompanyContext";
import { CreateAppointmentModal } from "./CreateAppointmentModal";
import { UpdatePaymentModal } from "./UpdatePaymentModal";
import { ConfirmationModal } from "../../shared";
import { Avatar } from "../../shared/Avatar/Avatar";
import {
  CarExpiredFollowUpReminder,
  getFollowUpItemsForAppointment,
} from "./CarExpiredFollowUpReminder";

interface AppointmentsCalendarViewProps {
  professionals: Professional[];
  selectedDate: string;
  onSlotClick: (slot: {
    professionalId: number;
    professionalName: string;
    date: string;
    time: string;
  }) => void;
  onRefresh?: () => void;
  followUpByClientId?: Map<number, FollowUpStatusItem[]>;
  onFollowUpScheduled?: () => void;
}

interface CalendarSlot {
  time: string;
  hour: number;
  minute: number;
  appointments: Appointment[];
  availableSlots: {
    professionalId: number;
    professionalName: string;
    slot: TimeSlot;
  }[];
}

export const AppointmentsCalendarView: React.FC<
  AppointmentsCalendarViewProps
> = ({
  professionals,
  selectedDate,
  onSlotClick,
  onRefresh,
  followUpByClientId,
  onFollowUpScheduled,
}) => {
  const { company } = useCompany();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [appointmentToEdit, setAppointmentToEdit] = useState<Appointment | null>(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [appointmentForPayment, setAppointmentForPayment] = useState<Appointment | null>(null);
  const [cancelConfirmationOpen, setCancelConfirmationOpen] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState<Appointment | null>(null);
  const [availableSlotsByProfessional, setAvailableSlotsByProfessional] =
    useState<Record<number, TimeSlot[]>>({});
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Reorder professionals so the one from localStorage appears first
  const sortedProfessionals = useMemo(() => {
    if (professionals.length === 0) return professionals;
    
    const savedProfessionalId = localStorage.getItem("adminDashboardSelectedProfessionalId");
    if (!savedProfessionalId) return professionals;
    
    const savedId = parseInt(savedProfessionalId, 10);
    const savedProfessional = professionals.find((p) => p.id === savedId);
    
    if (!savedProfessional) return professionals;
    
    // Put saved professional first, then the rest
    const otherProfessionals = professionals.filter((p) => p.id !== savedId);
    return [savedProfessional, ...otherProfessionals];
  }, [professionals]);

  // Fetch all slots for each professional
  useEffect(() => {
    const fetchAllSlots = async () => {
      if (!selectedDate || sortedProfessionals.length === 0) return;
      
      // Don't refresh if a modal is open to prevent closing it
      if (editModalOpen || paymentModalOpen) {
        return;
      }

      setLoadingSlots(true);
      const slotsMap: Record<number, TimeSlot[]> = {};

      // Loop through professionals and fetch slots
      for (const professional of sortedProfessionals) {
        try {
          const response = await availabilityApi.getAllSlots(
            professional.id,
            selectedDate
          );

          if (response.success && response.data) {
            const data = response.data;
            
            // Extract slots - assuming data is an array of slots or has a slots property
            const slots = Array.isArray(data) ? data : (data.slots || []);
            if (Array.isArray(slots)) {
              slotsMap[professional.id] = slots;
            }
          }
        } catch (error) {
          console.error(
            `Error fetching all slots for professional ${professional.id}:`,
            error
          );
        }
      }

      setAvailableSlotsByProfessional(slotsMap);
      setLoadingSlots(false);
    };

    fetchAllSlots();
  }, [selectedDate, sortedProfessionals, refreshTrigger, editModalOpen, paymentModalOpen]);


  // Generate time slots directly from API slots
  const timeSlots = useMemo(() => {
    // Early return if no data
    if (sortedProfessionals.length === 0) {
      return [];
    }

    // Collect all unique time slots from all professionals
    const timeSlotMap = new Map<string, CalendarSlot>();

    sortedProfessionals.forEach((professional) => {
      const professionalSlots = availableSlotsByProfessional[professional.id] || [];
      
      professionalSlots.forEach((slot) => {
        if (!slot.startTime) return;

        const timeString = slot.startTime;
        const time = parseTime(timeString);

        // Get or create calendar slot for this time
        let calendarSlot = timeSlotMap.get(timeString);
        if (!calendarSlot) {
          calendarSlot = {
            time: timeString,
            hour: time.hour,
            minute: time.minute,
            appointments: [],
            availableSlots: [],
          };
          timeSlotMap.set(timeString, calendarSlot);
        }

        // Check if slot has an appointment
        if (slot.appointment && slot.isAvailable === false) {
          calendarSlot.appointments.push(slot.appointment);
        } else if (slot.isAvailable !== false) {
          // Slot is available
          calendarSlot.availableSlots.push({
            professionalId: professional.id,
            professionalName: professional.name,
            slot: slot,
          });
        }
      });
    });

    // Convert map to array and sort by time
    const slots = Array.from(timeSlotMap.values()).sort((a, b) => {
      if (a.hour !== b.hour) {
        return a.hour - b.hour;
      }
      return a.minute - b.minute;
    });

    return slots;
  }, [sortedProfessionals, availableSlotsByProfessional]);

  const handleSlotClick = (
    professionalId: number,
    professionalName: string,
    time: string
  ) => {
    onSlotClick({
      professionalId,
      professionalName,
      date: selectedDate,
      time,
    });
  };

  if (sortedProfessionals.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-slate-400">
        Nenhum profissional disponível
      </div>
    );
  }

  return (
    <div className="w-full overflow-x-auto">
      <div className="min-w-full">
        {/* Header with professionals */}
        {sortedProfessionals.length > 0 && timeSlots.length > 0 && (
        <div
          className="grid gap-2 mb-2 sticky top-0 bg-white dark:bg-slate-800 z-10 pb-2 border-b border-gray-300 dark:border-slate-600"
          style={{
            gridTemplateColumns: `120px repeat(${sortedProfessionals.length}, minmax(150px, 1fr))`,
          }}
        >
          <div className="font-semibold text-sm text-gray-700 dark:text-slate-200 p-2 justify-center items-center flex mt-1">Horário</div>
          {sortedProfessionals.map((professional) => (
            <div
              key={professional.id}
              className="flex flex-col items-center justify-center gap-2 p-2 mt-1"
            >
              <Avatar
                text={professional.name}
                src={professional.picture}
                size="md"
                variant="blue"
                className="border-2 border-gray-200 dark:border-slate-600"
              />
              <div className="font-semibold text-sm text-gray-700 dark:text-slate-200 text-center">
                {professional.name}
              </div>
            </div>
          ))}
        </div>
        )}
        {/* Time slots */}
        <div className="space-y-1">
          {timeSlots.map((slot) => (
            <div
              key={slot.time}
              className="grid gap-2 hover:bg-gray-50 dark:hover:bg-slate-700/40 transition-colors rounded-lg"
              style={{
                gridTemplateColumns: `120px repeat(${sortedProfessionals.length}, minmax(150px, 1fr))`,
              }}
            >
              {/* Time label */}
              <div className="text-xs sm:text-sm text-gray-600 dark:text-slate-300 p-2 flex items-center font-medium justify-center">
                {formatTime(slot.time)}
              </div>

              {/* Professional columns */}
              {sortedProfessionals.map((professional) => {
                const appointment = slot.appointments.find(
                  (apt) => apt.professionalId === professional.id
                );
                const availableSlot = slot.availableSlots.find(
                  (slot) => slot.professionalId === professional.id
                );

                // Check if appointment is in the past
                const isPast = appointment ? (() => {
                  const appointmentDateTime = appointment.startTime
                    ? new Date(`${appointment.appointmentDate}T${appointment.startTime}`)
                    : new Date(`${appointment.appointmentDate}T12:00:00`);
                  const now = new Date();
                  return appointmentDateTime < now;
                })() : false;

                // Check if available slot is in the past
                const isSlotPast = availableSlot ? (() => {
                  const slotDateTime = new Date(`${selectedDate}T${slot.time}`);
                  const now = new Date();
                  return slotDateTime < now;
                })() : false;

                return (
                  <div
                    key={professional.id}
                    className="min-h-[60px] sm:min-h-[70px] p-1 border border-gray-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900/40"
                  >
                    {appointment ? (
                      <div
                        className={`${
                          isPast
                            ? "bg-slate-500 dark:bg-slate-600 text-white hover:bg-slate-600 dark:hover:bg-slate-500"
                            : "bg-sky-500 dark:bg-sky-600 text-white hover:bg-sky-600 dark:hover:bg-sky-500"
                        } p-2 rounded-lg text-xs cursor-pointer transition-colors h-full flex flex-col justify-center shadow-sm relative`}
                        onClick={(e) => {
                          if ((e.target as HTMLElement).closest("button")) {
                            return;
                          }
                          if (appointment.status && appointment.status.toLowerCase() !== "cancelled") {
                            setAppointmentToEdit(appointment);
                            setEditModalOpen(true);
                          }
                        }}
                      >
                        <div className="font-semibold truncate flex items-center gap-1">
                          {appointment.client?.name || "Cliente"}
                          {appointment.client && isBirthdayToday((appointment.client as any).birthday) && (
                            <span className="text-yellow-300 text-sm flex-shrink-0" title="Aniversário hoje!">
                              🎂
                            </span>
                          )}
                          {appointment.client ? (
                            <CarExpiredFollowUpReminder
                              items={getFollowUpItemsForAppointment(
                                followUpByClientId,
                                appointment.client.id,
                              )}
                              clientId={appointment.client.id}
                              clientName={appointment.client.name}
                              clientPhone={appointment.client.phoneNumber}
                              onScheduled={onFollowUpScheduled}
                            />
                          ) : null}
                        </div>
                        <div className="text-xs font-medium truncate opacity-95">
                          {appointment.service?.name || "Serviço"}
                        </div>
                        {appointment.price && (
                          <div className="text-xs font-medium">
                            R$ {appointment.price}
                          </div>
                        )}
                        {appointment.startTime && (
                          <div className="text-xs mt-0.5 flex flex-col gap-1">
                            <div className="flex items-center justify-between gap-1">
                              <span>{formatTime(appointment.startTime)}</span>
                            </div>
                            {/* Cancel and Payment buttons */}
                            <div className="flex items-center gap-1 sm:gap-1.5 justify-end flex-wrap">
                              {/* Cancel button */}
                              {appointment.status && 
                               appointment.status.toLowerCase() !== "cancelled" && (
                                <button
                                  className="px-1.5 sm:px-2 py-1 sm:py-1.5 rounded text-[9px] sm:text-[10px] font-medium bg-red-200/90 hover:bg-red-500 text-red-900 hover:text-white border border-red-400 hover:border-red-500 transition-colors flex items-center gap-0.5 sm:gap-1 flex-shrink-0 min-w-[24px] sm:min-w-0"
                                  title="Cancelar agendamento"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setAppointmentToCancel(appointment);
                                    setCancelConfirmationOpen(true);
                                  }}
                                >
                                  <span className="text-[10px] sm:text-xs">❌</span>
                                  <span className="hidden sm:inline">Cancelar</span>
                                </button>
                              )}
                              
                              {/* Payment */}
                              {appointment.payment ? (
                                <span
                                  className={`payment-button inline-block px-1.5 sm:px-2 py-1 sm:py-1.5 rounded text-[9px] sm:text-[10px] font-medium border flex-shrink-0 ${getPaymentMethodColor(appointment.payment)}`}
                                  title={`Pagamento: ${appointment.payment}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setAppointmentForPayment(appointment);
                                    setPaymentModalOpen(true);
                                  }}
                                >
                                  {appointment.payment}
                                </span>
                              ) : (
                                <button
                                  className="payment-button px-1.5 sm:px-2 py-1 sm:py-1.5 rounded text-[9px] sm:text-[10px] font-medium bg-white/90 hover:bg-white text-slate-800 border border-white/80 transition-colors flex items-center gap-0.5 sm:gap-1 flex-shrink-0 min-w-[24px] sm:min-w-0 shadow-sm"
                                  title="Adicionar pagamento"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setAppointmentForPayment(appointment);
                                    setPaymentModalOpen(true);
                                  }}
                                >
                                  <span className="text-[10px] sm:text-xs">💳</span>
                                  <span className="">Pagar</span>
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : availableSlot ? (
                      <button
                        type="button"
                        className={`w-full h-full border-2 border-dashed rounded-lg text-xs sm:text-sm font-medium p-2 transition-all cursor-pointer flex items-center justify-center shadow-sm hover:shadow-md ${
                          isSlotPast
                            ? "bg-slate-50 dark:bg-slate-800/80 hover:bg-slate-100 dark:hover:bg-slate-700 border-slate-300 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500 text-slate-600 dark:text-slate-400"
                            : "bg-emerald-50 dark:bg-emerald-950/50 hover:bg-emerald-100 dark:hover:bg-emerald-950 border-emerald-300 dark:border-emerald-700 hover:border-emerald-400 dark:hover:border-emerald-600 text-emerald-700 dark:text-emerald-300"
                        }`}
                        onClick={() =>
                          handleSlotClick(
                            professional.id,
                            professional.name,
                            slot.time
                          )
                        }
                      >
                        <span>✓ Disponível</span>
                      </button>
                    ) : (
                      <div className="w-full h-full bg-gray-50 dark:bg-slate-800/60 rounded-lg flex items-center justify-center p-2">
                        <span className="text-xs text-gray-400 dark:text-slate-500 text-center">
                          Indisponível
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {loadingSlots && (
          <div className="text-center py-8 text-sm text-gray-500 dark:text-slate-400">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 dark:border-slate-200 mr-2"></div>
            Carregando horários disponíveis...
          </div>
        )}

        {!loadingSlots && timeSlots.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl text-gray-400 dark:text-slate-400">📅</span>
            </div>
            <p className="text-sm sm:text-base text-gray-600 dark:text-slate-300 font-medium">
              Não há horários disponíveis para esta data
            </p>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-slate-400 mt-2">
              Por favor, escolha outra data ou verifique a disponibilidade dos profissionais
            </p>
          </div>
        )}
      </div>

      {/* Payment Modal */}
      {appointmentForPayment && (
        <UpdatePaymentModal
          isOpen={paymentModalOpen}
          onClose={() => {
            setPaymentModalOpen(false);
            setAppointmentForPayment(null);
          }}
          onSuccess={(updatedAppointment) => {
            if (updatedAppointment) {
              // Update the appointment directly in the state
              setAvailableSlotsByProfessional((prev) => {
                const updated = { ...prev };
                Object.keys(updated).forEach((professionalIdStr) => {
                  const professionalId = parseInt(professionalIdStr, 10);
                  updated[professionalId] = updated[professionalId].map((slot) => {
                    if (slot.appointment?.id === updatedAppointment.id) {
                      return {
                        ...slot,
                        appointment: updatedAppointment,
                      };
                    }
                    return slot;
                  });
                });
                return updated;
              });
            }
            setPaymentModalOpen(false);
            setAppointmentForPayment(null);
            if (onRefresh) {
              onRefresh();
            }
          }}
          appointment={appointmentForPayment}
        />
      )}

      {/* Edit Appointment Modal */}
      {appointmentToEdit && company && (
        <CreateAppointmentModal
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setAppointmentToEdit(null);
          }}
          onSuccess={() => {
            setEditModalOpen(false);
            setAppointmentToEdit(null);
            // Trigger slot refresh to fetch updated appointments
            setRefreshTrigger(prev => prev + 1);
            if (onRefresh) {
              onRefresh();
            }
          }}
          selectedDate={appointmentToEdit.appointmentDate}
          editingAppointment={{
            id: appointmentToEdit.id,
            clientId: appointmentToEdit.clientId,
            serviceId: appointmentToEdit.serviceId,
            professionalId: appointmentToEdit.professionalId,
            appointmentDate: appointmentToEdit.appointmentDate,
            startTime: appointmentToEdit.startTime,
            price: appointmentToEdit.price,
            clientCarId: appointmentToEdit.clientCarId,
          }}
        />
      )}

      {/* Cancel Confirmation Modal */}
      <ConfirmationModal
        isOpen={cancelConfirmationOpen}
        title="Cancelar Agendamento"
        message={`Tem certeza que deseja cancelar o agendamento de ${appointmentToCancel?.client?.name || "o cliente"}? Esta ação não pode ser desfeita.`}
        confirmText="Cancelar Agendamento"
        cancelText="Voltar"
        onConfirm={async () => {
          if (!appointmentToCancel) return;
          
          try {
            const response = await appointmentsApi.cancelAppointment(
              appointmentToCancel.id
            );

            if (response.success) {
              // Update the appointment directly in the state
              setAvailableSlotsByProfessional((prev) => {
                const updated = { ...prev };
                Object.keys(updated).forEach((professionalIdStr) => {
                  const professionalId = parseInt(professionalIdStr, 10);
                  updated[professionalId] = updated[professionalId].map((slot) => {
                    if (slot.appointment?.id === appointmentToCancel.id) {
                      return {
                        ...slot,
                        appointment: {
                          ...slot.appointment,
                          status: "cancelled",
                        },
                      };
                    }
                    return slot;
                  });
                });
                return updated;
              });
              setCancelConfirmationOpen(false);
              setAppointmentToCancel(null);
              if (onRefresh) {
                onRefresh();
              }
            }
          } catch (err) {
            console.error("Error canceling appointment:", err);
          }
        }}
        onCancel={() => {
          setCancelConfirmationOpen(false);
          setAppointmentToCancel(null);
        }}
        variant="danger"
      />
    </div>
  );
};
